import { CommonModule, DecimalPipe, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { URL_KEYS } from '@core/constants/url-keys.constants';
import { NotificationFacade } from '@core/facades/notification.facade';
import {
  BeneficiaryDetailsData,
  WalletTransactionData,
} from '@core/models/admin/account';
import { CommonService } from '@core/services/common.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { LoadingService } from '@core/services/loading.service';
import { TransactionsService } from '@core/services/transactions.service';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import { ButtonDirective } from '@shared/directives/button.directive';
import { ConfirmDialogComponent } from '@shared/ui/confirm-dialog/confirm-dialog.component';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import { SlideInRightModalComponent } from '@shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-approve-transaction',
  standalone: true,
  imports: [
    CommonModule,
    SlideInRightModalComponent,
    ButtonDirective,
    InputComponent,
    ReactiveFormsModule,
    ConfirmDialogComponent,
    NgxSkeletonLoaderModule,
    InputLabelComponent,
    BaseSelectDirective
  ],
  templateUrl: './approve-transaction.component.html',
  styleUrl: './approve-transaction.component.scss',
})
export class ApproveTransactionComponent implements OnInit, OnDestroy {
  private transactionService = inject(TransactionsService);
  private route = inject(ActivatedRoute);
  private toast = inject(NotificationFacade);
  private loadingService = inject(LoadingService);
  private commonService = inject(CommonService);
  private cdr = inject(ChangeDetectorRef);
  private confirmDialogService = inject(ConfirmDialogService);
  private fb = inject(FormBuilder)

  subs: Subscription[] = [];
  beneficiaryDetails!: BeneficiaryDetailsData | null;
  // transactionDetails!: WalletTransactionData;

  isDialogOpenSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private isDialogLoadingSubject$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private transactionDetailsSubject$ =
    new BehaviorSubject<WalletTransactionData | null>(null);
  private beneficiaryDetailsSubject$ =
    new BehaviorSubject<BeneficiaryDetailsData | null>(null);

  isDialogOpen$ = this.isDialogOpenSubject$.asObservable();
  isDialogLoading$ = this.isDialogLoadingSubject$.asObservable();
  transactionDetails$ = this.transactionDetailsSubject$.asObservable();
  beneficiaryDetails$ = this.beneficiaryDetailsSubject$.asObservable();

  accountBanks: BeneficiaryDetailsData[] = []

  isFetchingTransactionDetail$!: Observable<boolean>;
  isFetchingBankName$!: Observable<boolean>;

  transactionAmount!: string;
  bankAmount!: string;
  bankAccountName!: string;
  bankName?: string;
  accountId!: string;
  transactionId!: string;
  disableApproveAction!: boolean;
  bankList: { bankName: string; bankCode: string }[] = [];
  beneficiaryBankAccountId!: string;

  constructor() {
    this.isFetchingTransactionDetail$ = this.loadingService.getLoadingObservable("get-transaction-details");
    this.isFetchingBankName$ = this.loadingService.getLoadingObservable(
      URL_KEYS.COMMON.GET_BANK_NAME
    );
  }

  beneficiaryBank = this.fb.group({
    bank_account_id: ['', Validators.required]
  })

  ngOnInit(): void {
    this.accountId = this.route.snapshot.queryParams['account_id'];
    this.transactionId = this.route.snapshot.queryParams['tran_id'];

    if (this.accountId && this.transactionId) {
      // this.getBeneficiaryDetails(this.accountId);
      this.getTransactionDetails(this.transactionId);
      this.getBankList()
      this.watchBenificiaryInput()
    }
  }

  watchBenificiaryInput() {
    this.beneficiaryBank.get('bank_account_id')?.valueChanges.subscribe(bankId => {
      this.beneficiaryBankAccountId = bankId || ''
      const selectedBankDetails = this.accountBanks.find(bank => bank.id == this.beneficiaryBankAccountId)
      if (selectedBankDetails) {
        this.beneficiaryDetailsSubject$.next(selectedBankDetails);
        this.bankName = this.getBankName(selectedBankDetails.bank_code)
      }
      this.cdr.detectChanges()
    });
  }

  getTransactionDetails(tranId: string) {
    const sub = this.transactionService
      .getTransactionDetails(tranId)
      .subscribe({
        next: (response) => {
          let data = response.data;
          data.transaction_amount = Math.abs(data.transaction_amount);
          this.transactionDetailsSubject$.next(data);
          this.accountBanks = response.data?.account?.account_bank_info || []
        },
        error: () => {
          this.toast.error('Failed to fetch transaction details');
        },
      });

    this.subs.push(sub);
  }

  approveTransaction() {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    const sub = this.transactionService
      .processWithdrawalRequest({
        status: 'completed',
        transactionId: this.transactionId,
        accountId: this.accountId,
        bank_account_id: this.beneficiaryBankAccountId
      })
      .subscribe({
        next: () => {
          this.confirmDialogService.updateDialog({
            isDialogLoading: false,
            isOpen: false,
          });
          history.back();


          this.toast.success('Successfully approved transaction');
        },
        error: (err) => {
          this.confirmDialogService.updateDialog({ isDialogLoading: false, isOpen: false });
          this.toast.error('Failed to approve transaction');
        },
      });

    this.subs.push(sub);
  }

  getBankName(bankCode: string) {
    const bank = this.bankList.find(b => b.bankCode === bankCode);
    return bank ? bank.bankName : '';
  }

  getBankList() {
    const sub = this.commonService.getBankList().subscribe({
      next: (res: any) => {
        this.bankList = res.data;
      },
      error: () => {
        this.toast.error('Unable to fetch bank list.\nKindly refresh');
      },
    });
    this.subs.push(sub);
  }

  openDialog() {
    this.confirmDialogService.openDialog({
      isOpen: true,
      isDialogLoading: false,
      title: 'Yes, Approve Transaction',
      description: `Are you sure you want to approve this transaction? This action cannot be undone`,
      loadingText: 'Approving Transaction...',
      action: () => this.approveTransaction(),
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
